import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, notification} from 'antd';
import {AnchorLink, Hint, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, SubmitButton, Switch} from 'formik-antd';
import React, {useState} from 'react';
import {SectionProps} from '../utils/types';
import {
  AffiliateFieldsFragment,
  useDisableAffiliateProcessingMutation,
  useEnableAffiliateProcessingMutation,
  useUpdateAffiliatePayoutMethodMutation
} from 'api/generated';
import {formatIBAN} from 'lib/formaters';
import * as yup from 'yup';
import * as iban from 'ibantools';
import {checkAffiliateInformationVerified} from '../utils/utils';

export const PayoutMethodDetails: React.FC<SectionProps> = ({
  cardProps,
  formProps,
  affiliate,
  payoutMethod
}) => {
  const [isEditing, setEditing] = useState(false);
  const [updateAffiliatePayoutMethod] = useUpdateAffiliatePayoutMethodMutation();

  const [enableAffiliateProcessing] = useEnableAffiliateProcessingMutation();
  const [disableAffiliateProcessing] = useDisableAffiliateProcessingMutation();

  if (!payoutMethod || !affiliate)
    return (
      <Card {...cardProps} title="Payout method">
        <PropertyList omitEmpty properties={[]} />
      </Card>
    );

  const handleSubmit = async (values: AffiliateFieldsFragment) => {
    try {
      if (values.affiliate_group_id) {
        const verification = checkAffiliateInformationVerified(affiliate, payoutMethod!);
        if (verification.isValid) {
          await enableAffiliateProcessing({
            variables: {
              affiliateId: affiliate.id
            }
          });
        } else {
          notification.error({
            message: 'Invalid affiliate information',
            description:
              'Make sure all the information is filled and correct before enabling payouts.'
          });
        }
      } else if (values.affiliate_group_id !== affiliate.affiliate_group_id) {
        await disableAffiliateProcessing({
          variables: {
            affiliateId: affiliate.id
          }
        });
      }
      if (values.payout_methods?.[0]?.details?.iban !== payoutMethod?.details?.iban) {
        await updateAffiliatePayoutMethod({
          variables: {
            input: {
              affiliateId: affiliate?.id!,
              details: {
                iban: values.payout_methods?.[0]?.details?.iban,
                nif: payoutMethod?.details?.nif
              }
            }
          }
        });
        notification.success({
          message: 'Payout method updated',
          description: 'The payout method has been updated successfully.'
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error updating payout method',
        description: 'Has been an error while updating the payout method, please try again.'
      });
    } finally {
      setEditing(false);
    }
  };

  const initialValues: AffiliateFieldsFragment = {
    id: affiliate?.id!,
    payout_methods: [
      {
        id: payoutMethod?.id!,
        details: payoutMethod?.details
      }
    ],
    affiliate_group_id: affiliate?.affiliate_group_id
  };

  const validationSchema = yup.object().shape({
    details: yup.object().shape({
      iban: yup.string().test('iban', 'Invalid IBAN', (value) => !value || iban.isValidIBAN(value!))
    })
  });

  return (
    <>
      <Card
        {...cardProps}
        title="Payout settings"
        extra={
          isEditing ? null : (
            <AnchorLink onClick={() => setEditing(true)}>
              <EditOutlined /> Edit data
            </AnchorLink>
          )
        }>
        {isEditing ? (
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}>
            <Form {...formProps}>
              <FormItem name="payout_methods[0].details.iban" label="IBAN">
                <Input name="payout_methods[0].details.iban" placeholder="IBAN" />
              </FormItem>
              <FormItem
                hasFeedback={false}
                name="affiliate_group_id"
                label={
                  <Hint
                    title="Enable payouts"
                    message="This will enable payouts for the affiliate. Make sure all the information is filled and correct before enabling payouts."
                  />
                }>
                <Switch name="affiliate_group_id" />
              </FormItem>
              <FormItem
                wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
                name="submit">
                <Stack>
                  <Button onClick={() => setEditing(false)}>Cancel</Button>
                  <SubmitButton>Submit</SubmitButton>
                </Stack>
              </FormItem>
            </Form>
          </Formik>
        ) : (
          <PropertyList
            omitEmpty
            properties={[
              {
                label: 'IBAN',
                copyable: {text: payoutMethod?.details?.iban!},
                value: formatIBAN(payoutMethod?.details?.iban, true)
              }
            ]}
          />
        )}
      </Card>
    </>
  );
};
